import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import AddSocialLinkModal from '../components/AddSocialLinkModal';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200',
  approved:  'bg-blue-50 text-blue-600 border border-blue-200',
  monitored: 'bg-green-50 text-green-700 border border-green-200',
  verified:  'bg-purple-50 text-purple-700 border border-purple-200',
  rejected:  'bg-red-50 text-[#c62828] border border-red-200',
  closed:    'bg-gray-100 text-[#8a8a8a] border border-[#e5e5e5]',
};

const Proposals = () => {
  const { profile, user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);

  useEffect(() => { loadProposals(); }, []);

  const loadProposals = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/proposals');
      setProposals(data || []);
    } catch (error) {
      alert('Error loading proposals: ' + (error.message || 'Unknown error'));
      setProposals([]);
    } finally { setLoading(false); }
  };

  const handleApprove = async (proposalId) => {
    if (!window.confirm('Approve proposal ini?')) return;
    try {
      await api.patch(`/api/proposals/${proposalId}/approve`, {});
      loadProposals();
    } catch (error) { alert('Error: ' + error.message); }
  };

  const handleReject = async (proposalId) => {
    if (!window.confirm('Tolak proposal ini?')) return;
    try {
      await api.patch(`/api/proposals/${proposalId}/reject`, {});
      loadProposals();
    } catch (error) { alert('Error: ' + error.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Proposals</h1>
        {(profile?.role === 'hunter' || profile?.role === 'admin') && (
          <Link to="/proposals/create"
            className="inline-flex items-center justify-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto">
            + Submit Proposal
          </Link>
        )}
      </div>

      {proposals.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a]">Tidak ada proposal ditemukan.</p>
          {(profile?.role === 'hunter' || profile?.role === 'admin') && (
            <Link to="/proposals/create" className="mt-2 inline-block text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium">
              Submit proposal pertama
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden" style={card}>
          <div className="divide-y divide-[#e5e5e5]">
            {proposals.map((proposal) => {
              const status = proposal.display_status || proposal.status;
              return (
                <div key={proposal.id} className="px-5 py-4 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Campaign name + status */}
                      <div className="flex items-center flex-wrap gap-2 mb-1.5">
                        <Link to={`/proposals/${proposal.id}`}
                          className="text-[14px] font-semibold text-[#3b3b3b] hover:text-[#3fb5d2] transition-colors">
                          {proposal.campaigns?.name || 'Unknown Campaign'}
                        </Link>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-[#646464]'}`}>
                          {status}
                        </span>
                      </div>

                      {/* Submitted by (admin view) */}
                      {profile?.role !== 'hunter' && (
                        <p className="text-[12px] text-[#8a8a8a] mb-1">
                          By {proposal.user_profiles?.full_name || proposal.user_profiles?.email || '—'}
                        </p>
                      )}

                      {/* Social media link */}
                      {proposal.social_media_link && (
                        <div className="flex items-center gap-2 mt-1">
                          {proposal.social_media_platform && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
                              {proposal.social_media_platform}
                            </span>
                          )}
                          <a href={proposal.social_media_link} target="_blank" rel="noopener noreferrer"
                            className="text-[12px] text-[#3fb5d2] hover:text-[#2a798c] truncate max-w-xs transition-colors">
                            {proposal.social_media_link}
                          </a>
                        </div>
                      )}

                      {/* Add social link CTA for hunter on verified proposals */}
                      {proposal.status === 'verified' && !proposal.social_media_link && profile?.role === 'hunter' && proposal.user_id === user?.id && (
                        <button onClick={() => setSelectedProposal(proposal)}
                          className="mt-2 text-[12px] text-[#3fb5d2] hover:text-[#2a798c] font-medium transition-colors">
                          + Tambah social media link
                        </button>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {(profile?.role === 'campaign_manager' || profile?.role === 'admin' || profile?.role === 'admin_operator') && proposal.status === 'pending' && (
                        <>
                          <button onClick={() => handleApprove(proposal.id)}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors">
                            Approve
                          </button>
                          <button onClick={() => handleReject(proposal.id)}
                            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-[#c62828] border border-red-200 hover:bg-red-100 transition-colors">
                            Reject
                          </button>
                        </>
                      )}
                      <Link to={`/proposals/${proposal.id}`}
                        className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] border border-[#3fb5d2]/40 px-3 py-1.5 rounded-lg transition-colors">
                        Detail
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedProposal && (
        <AddSocialLinkModal
          proposal={selectedProposal}
          onClose={() => setSelectedProposal(null)}
          onSuccess={loadProposals}
        />
      )}
    </div>
  );
};

export default Proposals;
